import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {environment} from "@environments/environment";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {Result} from "@domain/common/http/result";
import {DateApplication, FraisMissionDetail, IndemniteDetail} from "@domain/admin/frais/indemnites/frais-mission-detail";
import {PourcentageDomaine} from "@domain/admin/frais/indemnites/indemnites-parametres";
import { FraisMissionDuplicationRequest } from '@domain/admin/frais/indemnites/frais-mission-duplication-request';

/**
 * Requête de mise à jour de la période d'un groupe de frais mission
 */
export interface UpdatePeriodeFraisMissionRequest {
    idTaux: number;
    periodeMin: number;
    periodeMax: number;
}

/**
 * Service de gestion des frais de mission
 */
@Injectable({
    providedIn: 'root'
})
export class FraisMissionService {

    constructor(private http: HttpClient) {}

    /**
     * Charge un frais de mission par son identifiant
     * @param idTaux Identifiant du frais
     * @returns Observable contenant le détail du frais de mission
     */
    loadFraisMission(idTaux: number): Observable<FraisMissionDetail> {
        // Appel au backend pour charger le frais de mission
        return this.http.post<Result>(`${environment.baseUrl}/controller/FraisMission/load/${idTaux}`, null)
            .pipe(map(result => {
                //Récupération de la devise entreprise si pas de devise sur le frais
                if (!result.data.fraisMission.devise) {
                    result.data.fraisMission.devise = result.data.deviseEntreprise;
                }
                //Mapper les données backend vers le modèle FraisMissionDetail
                return this.mapToFraisMissionDetail(result.data.fraisMission);
            }));
    }

    /**
     * Charge l'historique des dates d'application d'un frais de mission
     * @param idTaux Identifiant du frais
     * @param includeItself Inclure le frais lui-même dans la liste
     * @returns Observable contenant l'historique des dates d'application
     */
    getHistoriqueFraisMission(idTaux: number, includeItself: boolean = true): Observable<DateApplication[]> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/IJ/getHistoriqueFraisMission/${idTaux}`, includeItself)
            .pipe(map(result => this.mapToDateApplicationList(result.data.listeHistorique)));
    }

    /**
     * Sauvegarde un frais de mission
     * @param dateApplication Date d'application à sauvegarder
     * @returns Observable contenant le résultat de la sauvegarde
     */
    saveFraisMission(dateApplication: DateApplication): Observable<Result> {
        //Mapper vers la structure attendue par le backend
        const fraisMission = this.mapToBackendFraisMission(dateApplication);
        return this.http.put<Result>(`${environment.baseUrl}/controller/FraisMission/save`, fraisMission);
    }

    /**
     * Supprime un frais de mission
     * @param idTaux Identifiant du frais à supprimer
     * @returns Observable contenant le résultat de la suppression
     */
    deleteFraisMission(idTaux: number): Observable<Result> {
        return this.http.delete<Result>(`${environment.baseUrl}/controller/FraisMission/delete/${idTaux}`);
    }

    /**
     * Charge la liste des pourcentages par domaine
     * @returns Observable contenant la liste des pourcentages par domaine
     */
    loadListePourcentagesDomaine(): Observable<PourcentageDomaine[]> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/IJ/listePourcentagesDomaine`, null)
            .pipe(map(result => result.data.listePourcentages));
    }

    /**
     * Mappe les données backend vers FraisMissionDetail
     */
    private mapToFraisMissionDetail(data: any): FraisMissionDetail {
        return new FraisMissionDetail(data);
    }

    /**
     * Mappe les données backend vers une liste de DateApplication
     * @param data Données backend
     * @returns Liste de DateApplication
     */
    private mapToDateApplicationList(data: any): DateApplication[] {
        const dataList = data?.listeResultats || data?.listeFraisMission || data || [];
        if (!Array.isArray(dataList)) return [];
        return dataList.map(item => new DateApplication(item));
    }

    /**
     * Mappe DateApplication vers la structure backend FraisMission
     * @param dateApp DateApplication
     * @returns Structure backend FraisMission
     */
    private mapToBackendFraisMission(dateApp: DateApplication): any {
        return {
            idTaux: dateApp.idTaux || 0,
            dateApplication: dateApp.dateApplication,
            montant: dateApp.montant,
            devise: dateApp.devise,
            periodeMin: dateApp.periodeMin,
            periodeMax: dateApp.periodeMax,
            libelleSpecial: dateApp.libelleSpecial,
            fraisMissionRegion: dateApp.fraisMissionRegion ? {
                idTaux: dateApp.idTaux || 0,
                idZone: dateApp.fraisMissionRegion.idZone,
                idPays: dateApp.fraisMissionRegion.idPays,
                idRegion: dateApp.fraisMissionRegion.idRegion,
                idVille: dateApp.fraisMissionRegion.idVille
            } : null,
            listeFraisMissionIndemnite: dateApp.listeFraisMissionIndemnite
                ?.filter(i => i.montant != null && i.montant > 0)
                .map(i => ({
                    idDomaine: i.idDomaine,
                    montant: i.montant,
                    pourcentage: i.pourcentage
                }))
        };
    }

    /**
     * Supprime un groupe de frais mission
     * @param idTaux Identifiant du taux permettant d'identifier le groupe
     * @returns Observable contenant le résultat de la suppression
     */
    deleteGroupeFraisMission(idTaux: number): Observable<Result> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/FraisMission/deleteGroupe`, idTaux);
    }

    /**
     * Duplique un groupe de frais mission
     * @param payload Données de duplication contenant l'identifiant source et les nouveaux paramètres
     * @returns Observable contenant le résultat de la duplication
     */
    public duplicateGroupeFraisMission(payload: FraisMissionDuplicationRequest): Observable<Result> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/FraisMission/duplicateGroupe`, payload);
    }

    /**
     * Met à jour la période de validité sur toutes les dates d'application liées
     * @param payload Données de mise à jour de période
     * @returns Résultat de la mise à jour
     */
    public updatePeriodeGroupeFraisMission(payload: UpdatePeriodeFraisMissionRequest): Observable<Result> {
        return this.http.put<Result>(`${environment.baseUrl}/controller/FraisMission/updatePeriode`, payload);
    }


}